/**
 * Story 短剧本人片库只读子域（F 独占新建）。
 *
 * 只做三件事：读 B 已受理的本人任务投影、按稳定集 ID 读本人一集、流式取本人私有媒体字节。
 * 不提供任何写入、公开、发布或撤回方法——发布/撤回只能走 06 的真实服务，私有不变量不在前端改。
 * 媒体走 transport.binary：每次播放由服务端重新鉴权，前端拿不到对象键、签名 URL 或公共地址。
 *
 * 集成钩子（由 Iris 唯一登记给 B 在可视化 API 聚合中接线，F 不改 A 的 novel/index.js 与共享 visual-game.js）：
 *   import { createStoryFilmLibraryApi } from './domains/story-film-library.js'
 *   storyFilmLibrary: createStoryFilmLibraryApi(transport),
 */

const PREFIX = '/api/visual-game/me/story-film'

/**
 * @param {{ request: Function, binary: Function }} transport
 * @param {{ prefix?: string }} [options]
 */
export function createStoryFilmLibraryApi(transport, { prefix = PREFIX } = {}) {
  if (!transport || typeof transport.request !== 'function' || typeof transport.binary !== 'function') {
    throw new Error('createStoryFilmLibraryApi: transport.request and transport.binary are required')
  }
  return {
    /**
     * 本人 Story 集的拍摄状态投影（B 受理/任务）；不含来源正文、不含对象键。
     * @param {{ cursor?: string|null, limit?: number|null }} [query]
     */
    listProduction({ cursor = null, limit = null } = {}) {
      const params = new URLSearchParams()
      if (cursor !== null && cursor !== '') params.set('cursor', String(cursor))
      if (limit !== null && limit !== undefined) params.set('limit', String(limit))
      const query = params.toString()
      return transport.request('GET', `${prefix}/production${query ? `?${query}` : ''}`, {
        parse: parseProductionList,
      })
    },
    /** @param {string} episodeId */
    getEpisode(episodeId) {
      return transport.request('GET', `${prefix}/episodes/${encodeURIComponent(requireId(episodeId))}`, {
        parse: parseEpisodeDetail,
      })
    },
    /**
     * 本人私有成片字节；未就绪/他人/匿名一律抛 NOT_FOUND，DOM 里只拿到 blob URL。
     * @param {string} episodeId
     */
    episodeMedia(episodeId) {
      return transport.binary('GET', `${prefix}/episodes/${encodeURIComponent(requireId(episodeId))}/media`, {
        timeoutMs: 60000,
      })
    },
  }
}

function parseProductionList(value) {
  if (!isObject(value) || !Array.isArray(value.episodes)) {
    throw new Error('Story film production response must contain episodes')
  }
  return {
    episodes: value.episodes.map(parseProductionEpisode),
    next_cursor: value.next_cursor === null || value.next_cursor === undefined ? null : String(value.next_cursor),
  }
}

export function parseProductionEpisode(row) {
  if (!isObject(row)) throw new Error('Story film production episode must be an object')
  return {
    episode_id: requireString(row.episode_id, 'episode_id'),
    submission_id: requireNumber(row.submission_id, 'submission_id'),
    task_id: row.task_id === null || row.task_id === undefined ? null : requireNumber(row.task_id, 'task_id'),
    status: requireString(row.status, 'status'),
    task_status: row.task_status === null || row.task_status === undefined ? null : String(row.task_status),
    failure_code: row.failure_code === null || row.failure_code === undefined ? null : String(row.failure_code),
    price_points: requireNumber(row.price_points, 'price_points'),
    currency: requireString(row.currency, 'currency'),
    media_ready: row.media_ready === true,
    media_version: row.media_version === null || row.media_version === undefined ? null : String(row.media_version),
    title: optionalNullableString(row.title, 'title'),
    summary: optionalNullableString(row.summary, 'summary'),
    duration_ms: optionalPositiveNullableInteger(row.duration_ms, 'duration_ms'),
    created_at: requireString(row.created_at, 'created_at'),
    updated_at: requireString(row.updated_at, 'updated_at'),
    // 服务端钟的精确就绪时刻（ISO 8601）：旧响应**缺失**为 null；给了非 ISO 值一律抛错 fail closed，
    // 不用 updated_at 冒充，也不把空串静默当成“没有就绪时刻”。
    ready_at: optionalIsoNullableString(row.ready_at, 'ready_at'),
    // 修订版读模型（本批可选）：当前应播放的那一版与“是否正在修订”；旧响应缺失为 null。
    playable_submission_id: optionalPositiveNullableInteger(row.playable_submission_id, 'playable_submission_id'),
    correcting: row.correcting === null || row.correcting === undefined ? null : optionalBoolean(row.correcting),
  }
}

export function parseEpisodeDetail(value) {
  if (!isObject(value) || !isObject(value.episode)) {
    throw new Error('Story film episode response must contain episode')
  }
  const episode = value.episode
  return {
    publishing: value.publishing?.can_publish === true && typeof value.publishing.asset_id === 'string' && /^story-film:[a-f0-9-]{36}:[1-9]\d*$/.test(value.publishing.asset_id)
      ? { can_publish: true, asset_id: value.publishing.asset_id }
      : { can_publish: false, asset_id: null },
    // 服务端钟的入场时刻：缺失为 null，给了非 ISO 值则 fail closed（调用方不得拿设备钟兜底）。
    server_now: optionalIsoNullableString(value.server_now, 'server_now'),
    episode: {
      episode_id: requireString(episode.episode_id, 'episode_id'),
      film_id: requireString(episode.film_id, 'film_id'),
      film_title: requireString(episode.film_title, 'film_title'),
      run_id: requireString(episode.run_id, 'run_id'),
      branch_id: episode.branch_id === null || episode.branch_id === undefined ? null : String(episode.branch_id),
      season_id: requireString(episode.season_id, 'season_id'),
      season_no: requireNumber(episode.season_no, 'season_no'),
      episode_no: requireNumber(episode.episode_no, 'episode_no'),
      play_language: requireString(episode.play_language, 'play_language'),
      source_version: requireString(episode.source_version, 'source_version'),
      prepared_at: requireString(episode.prepared_at, 'prepared_at'),
    },
    production: value.production === null || value.production === undefined
      ? null
      : parseProductionEpisode(value.production),
  }
}

function requireId(value) {
  if (typeof value !== 'string' || value.trim() === '') {
    throw new Error('Story film library call requires a non-empty episode id')
  }
  return value.trim()
}

function requireString(value, field) {
  if (typeof value !== 'string' || value.trim() === '') {
    throw new Error(`Story film library response field is invalid: ${field}`)
  }
  return value
}

function optionalNullableString(value, field) {
  if (value === null || value === undefined) return null
  if (typeof value !== 'string') throw new Error(`Story film library response field is invalid: ${field}`)
  return value.trim() === '' ? null : value
}

/**
 * 服务端钟时间字段：缺失或显式 null → null；给了值就必须是能被 Date.parse 识别的 ISO 8601 串，
 * 否则抛错。S01A 在场判定用它在同一钟上比较，格式错必须 fail closed 而不是猜。
 */
function optionalIsoNullableString(value, field) {
  if (value === null || value === undefined) return null
  if (typeof value !== 'string' || !Number.isFinite(Date.parse(value))) {
    throw new Error(`Story film library response field is invalid: ${field}`)
  }
  return value
}

/** 三态布尔：缺失或非布尔一律 null，由调用方决定默认，不擅自当 false。 */
function optionalBoolean(value) {
  if (value === null || value === undefined) return null
  if (typeof value !== 'boolean') throw new Error('Story film library response field is invalid: boolean')
  return value
}

function optionalPositiveNullableInteger(value, field) {
  if (value === null || value === undefined) return null
  const number = Number(value)
  if (!Number.isInteger(number) || number <= 0) throw new Error(`Story film library response field is invalid: ${field}`)
  return number
}

function requireNumber(value, field) {
  const number = Number(value)
  if (!Number.isFinite(number)) throw new Error(`Story film library response field is invalid: ${field}`)
  return number
}

function isObject(value) {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value)
}
